import React, { useState, useContext, useEffect } from "react";
import { useLocation, Routes, Route } from "react-router-dom";
import { observer } from "mobx-react-lite";
import AuthContext from "../../context/AuthProvider";
import { useStore } from "../../stores/store";

import NotFound from "../NotFound";

import TopHeader from "./TopHeader";
import Sidebar from "./Sidebar";
import MainFooter from "./MainFooter";

import { Home, HomeSubcategories, HomeToolsLegacy } from "../Home";

import SettingsDashboard from "../Admin/Dashboard/Dashboard";
import FacilityDashboard from "../Facility/Dashboard/Dashboard";

import AddUser from "../Admin/User/Add";
import UserList from "../Admin/User/List";
import EditUser from "../Admin/User/Edit";

import ChangeUserPassword from "../Admin/User/ChangePassword"

import AddRole from "../Admin/Role/Add";
import RoleList from "../Admin/Role/List";
import EditRole from "../Admin/Role/Edit";

import UserApprovalsList from "../Admin/UserApprovals/List";
import ContentApprovalsList from "../Admin/ContentApprovals/List";

import PanelList from "../Admin/Panel/List";
import AddPanel from "../Admin/Panel/Add";
import EditPanel from "../Admin/Panel/Edit";

import CategoryList from "../Admin/Category/List";
import AddCategory from "../Admin/Category/Add";
import EditCategory from "../Admin/Category/Edit";

import SubcategoryList from "../Admin/Subcategory/List";
import AddSubcategory from "../Admin/Subcategory/Add";
import EditSubcategory from "../Admin/Subcategory/Edit";

import TypeOfToolList from "../Admin/TypeOfTool/List";
import AddTypeOfTool from "../Admin/TypeOfTool/Add";
import EditTypeOfTool from "../Admin/TypeOfTool/Edit";

import ToolPassportList from "../Admin/ToolPassport/List";
import AddToolPassport from "../Admin/ToolPassport/Add";
import EditToolPassport from "../Admin/ToolPassport/Edit";

import CriteriaList from "../Admin/Criteria/List";
import AddCriteria from "../Admin/Criteria/Add";
import EditCriteria from "../Admin/Criteria/Edit";

import CriteriaValueList from "../Admin/CriteriaValue/List";
import AddCriteriaValue from "../Admin/CriteriaValue/Add";
import EditCriteriaValue from "../Admin/CriteriaValue/Edit";

import ToolFeatureList from "../Admin/ToolFeature/List";
import AddToolFeature from "../Admin/ToolFeature/Add";
import EditToolFeature from "../Admin/ToolFeature/Edit";

import ToolSelectionList from "../Admin/ToolSelection/Selection";

import { Manager as CriteriaManager } from "../Admin/CriteriaManager";

import { MethodComparison } from "../Admin/Research/MethodComparison";

// Content Management Admin Components
import ResearchPublicationsList from "../Admin/ContentManagement/ResearchPublications/List";
import AddResearchPublication from "../Admin/ContentManagement/ResearchPublications/Add";
import EditResearchPublication from "../Admin/ContentManagement/ResearchPublications/Edit";

import ResearchTeamList from "../Admin/ContentManagement/ResearchTeam/List";
import AddResearchTeamMember from "../Admin/ContentManagement/ResearchTeam/Add";
import EditResearchTeamMember from "../Admin/ContentManagement/ResearchTeam/Edit";

import PlatformHistoryList from "../Admin/ContentManagement/PlatformHistory/List";
import AddPlatformHistory from "../Admin/ContentManagement/PlatformHistory/Add";
import EditPlatformHistory from "../Admin/ContentManagement/PlatformHistory/Edit";

import SystemVersionsList from "../Admin/ContentManagement/SystemVersions/List";
import AddSystemVersion from "../Admin/ContentManagement/SystemVersions/Add";
import EditSystemVersion from "../Admin/ContentManagement/SystemVersions/Edit";

import KeyFeaturesList from "../Admin/ContentManagement/KeyFeatures/List";
import AddKeyFeature from "../Admin/ContentManagement/KeyFeatures/Add";
import EditKeyFeature from "../Admin/ContentManagement/KeyFeatures/Edit";

import MCDMMethodsList from "../Admin/ContentManagement/MCDMMethods/List";
import AddMCDMMethod from "../Admin/ContentManagement/MCDMMethods/Add";
import EditMCDMMethod from "../Admin/ContentManagement/MCDMMethods/Edit";

import SubmitToolPassport from "../User/Submissions/SubmitToolPassport";
import SubmitCriteria from "../User/Submissions/SubmitCriteria";
import SubmitToolFeature from "../User/Submissions/SubmitToolFeature";
import MySubmissions from "../User/Submissions/MySubmissions";
import MyTools from "../User/Submissions/MyTools";
import MyCriteria from "../User/Submissions/MyCriteria";
import MyToolFeatures from "../User/Submissions/MyToolFeatures";
import EditMyTool from "../User/Submissions/EditMyTool";
import MyRequests from "../User/Submissions/MyRequests";
import AboutContent from "./AboutContent";
import HelpContent from "./HelpContent";
import ToolsCatalogContent from "./ToolsCatalogContent";
import ResearchPublicationsContent from "./ResearchPublicationsContent";

const MainLayout = () => {
    const { auth } = useContext(AuthContext);
    const { mainStore } = useStore();

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const location = useLocation();
    const isHome = location.pathname === '/Home' || location.pathname === '/';
    const isTools = location.pathname.startsWith('/Tools');
    const isAbout = location.pathname === '/About';
    const isHelp = location.pathname === '/Help';
    const isToolsCatalog = location.pathname === '/ToolsCatalog';
    const isResearchPublications = location.pathname === '/ResearchPublications';
    const isResearch = location.pathname.startsWith('/Research');
    const isUserSubmission = location.pathname === '/MySubmissions' || location.pathname === '/MyTools' || location.pathname.startsWith('/MyTools/') || location.pathname === '/MyCriteria' || location.pathname === '/MyToolFeatures' || location.pathname === '/MyRequests' || location.pathname === '/SubmitToolPassport' || location.pathname === '/SubmitCriteria' || location.pathname === '/SubmitToolFeature';

    // Set menuType based on current route
    useEffect(() => {
        const path = location.pathname;

        if (path === '/' || path === '/Home' || path.startsWith('/Tools')) {
            mainStore.setMenuType(mainStore.HomeMenuType);
        } else if (path.startsWith('/User/') || path.startsWith('/Role/') ||
                   path.startsWith('/UserApprovals/') || path.startsWith('/ContentApprovals/') ||
                   path === '/Settings/Dashboard') {
            mainStore.setMenuType(mainStore.SettingsDashboardMenuType);
        } else if (path.startsWith('/Panel/') || path.startsWith('/Category/') ||
                   path.startsWith('/Subcategory/') || path.startsWith('/TypeOfTool/') ||
                   path.startsWith('/ToolPassport/')) {
            mainStore.setMenuType(mainStore.PanelMenuType);
        } else if (path.startsWith('/Criteria/') || path.startsWith('/CriteriaValue/') ||
                   path.startsWith('/ToolFeature/') || path.startsWith('/ToolSelection/') ||
                   path === '/CriteriaManager') {
            mainStore.setMenuType(mainStore.CriteriaMenuType);
        } else if (path.startsWith('/Research/')) {
            mainStore.setMenuType(mainStore.ResearchMenuType);
        } else if (path === '/MySubmissions' || path === '/MyTools' || path.startsWith('/MyTools/') || path === '/MyCriteria' || path === '/MyToolFeatures' || path === '/MyRequests' || path === '/SubmitToolPassport' || path === '/SubmitCriteria' || path === '/SubmitToolFeature') {
            mainStore.setMenuType(mainStore.UserSubmissionMenuType);
        } else if (path.startsWith('/Admin/')) {
            // Content Management routes - set menu type to Administration
            mainStore.setMenuType(mainStore.SettingsDashboardMenuType);
        }
    }, [location.pathname, mainStore]);

    // Shared routes accessible by both admin and user roles
    const sharedRoutes = [
        { path: "/", element: <Home /> },
        { path: "/Home", element: <Home /> },
        { path: "/Tools/:panelId/:categoryId", element: <HomeSubcategories /> },
        { path: "/Tools/:panelId/:categoryId/:subcategoryId", element: <HomeToolsLegacy /> },
        { path: "/ToolSelection/Selection", element: <ToolSelectionList /> },
        { path: "/ToolSelection/Selection/:panelid/:catid/:id", element: <ToolSelectionList /> },
        { path: "/About", element: <AboutContent /> },
        { path: "/Help", element: <HelpContent /> },
        { path: "/ToolsCatalog", element: <ToolsCatalogContent /> },
        // Research - accessible by both admin and user roles
        { path: "/Research/MethodComparison", element: <MethodComparison /> },
    ];

    // Admin-only routes
    const adminRoutes = [
        { path: "/Settings/Dashboard", element: <SettingsDashboard /> },
        { path: "/Facility/Dashboard", element: <FacilityDashboard /> },

        // Settings
        { path: "/User/Add", element: <AddUser /> },
        { path: "/User/List", element: <UserList /> },
        { path: "/User/Edit/:id", element: <EditUser /> },
        { path: "/User/ChangePassword/:id", element: <ChangeUserPassword /> },

        { path: "/Role/Add", element: <AddRole /> },
        { path: "/Role/List", element: <RoleList /> },
        { path: "/Role/Edit/:id", element: <EditRole /> },

        { path: "/UserApprovals/List", element: <UserApprovalsList /> },
        { path: "/ContentApprovals/List", element: <ContentApprovalsList /> },

        { path: "/Panel/List", element: <PanelList /> },
        { path: "/Panel/Add", element: <AddPanel /> },
        { path: "/Panel/Edit/:id", element: <EditPanel /> },

        { path: "/Category/List", element: <CategoryList /> },
        { path: "/Category/Add", element: <AddCategory /> },
        { path: "/Category/Edit/:id", element: <EditCategory /> },

        { path: "/Subcategory/List", element: <SubcategoryList /> },
        { path: "/Subcategory/Add", element: <AddSubcategory /> },
        { path: "/Subcategory/Edit/:id", element: <EditSubcategory /> },

        { path: "/TypeOfTool/List", element: <TypeOfToolList /> },
        { path: "/TypeOfTool/Add", element: <AddTypeOfTool /> },
        { path: "/TypeOfTool/Edit/:id", element: <EditTypeOfTool /> },

        { path: "/ToolPassport/List", element: <ToolPassportList /> },
        { path: "/ToolPassport/Add", element: <AddToolPassport /> },
        { path: "/ToolPassport/Edit/:id", element: <EditToolPassport /> },

        { path: "/Criteria/List", element: <CriteriaList /> },
        { path: "/Criteria/Add", element: <AddCriteria /> },
        { path: "/Criteria/Edit/:id", element: <EditCriteria /> },

        { path: "/CriteriaValue/List", element: <CriteriaValueList /> },
        { path: "/CriteriaValue/Add", element: <AddCriteriaValue /> },
        { path: "/CriteriaValue/Edit/:id", element: <EditCriteriaValue /> },

        { path: "/ToolFeature/List", element: <ToolFeatureList /> },
        { path: "/ToolFeature/Add", element: <AddToolFeature /> },
        { path: "/ToolFeature/Edit/:id", element: <EditToolFeature /> },

        { path: "/CriteriaManager", element: <CriteriaManager /> },

        // Content Management Routes
        { path: "/Admin/ResearchPublications", element: <ResearchPublicationsList /> },
        { path: "/Admin/ResearchPublications/Add", element: <AddResearchPublication /> },
        { path: "/Admin/ResearchPublications/Edit/:id", element: <EditResearchPublication /> },

        { path: "/Admin/ResearchTeam", element: <ResearchTeamList /> },
        { path: "/Admin/ResearchTeam/Add", element: <AddResearchTeamMember /> },
        { path: "/Admin/ResearchTeam/Edit/:id", element: <EditResearchTeamMember /> },

        { path: "/Admin/PlatformHistory", element: <PlatformHistoryList /> },
        { path: "/Admin/PlatformHistory/Add", element: <AddPlatformHistory /> },
        { path: "/Admin/PlatformHistory/Edit/:id", element: <EditPlatformHistory /> },

        { path: "/Admin/SystemVersions", element: <SystemVersionsList /> },
        { path: "/Admin/SystemVersions/Add", element: <AddSystemVersion /> },
        { path: "/Admin/SystemVersions/Edit/:id", element: <EditSystemVersion /> },

        { path: "/Admin/KeyFeatures", element: <KeyFeaturesList /> },
        { path: "/Admin/KeyFeatures/Add", element: <AddKeyFeature /> },
        { path: "/Admin/KeyFeatures/Edit/:id", element: <EditKeyFeature /> },

        { path: "/Admin/MCDMMethods", element: <MCDMMethodsList /> },
        { path: "/Admin/MCDMMethods/Add", element: <AddMCDMMethod /> },
        { path: "/Admin/MCDMMethods/Edit/:id", element: <EditMCDMMethod /> },
    ];

    // Routes accessible by both user and admin roles
    const userRoutes = [
        { path: "/SubmitToolPassport", element: <SubmitToolPassport /> },
        { path: "/SubmitCriteria", element: <SubmitCriteria /> },
        { path: "/SubmitToolFeature", element: <SubmitToolFeature /> },
        { path: "/MySubmissions", element: <MySubmissions /> },
        { path: "/MyTools", element: <MyTools /> },
        { path: "/MyCriteria", element: <MyCriteria /> },
        { path: "/MyToolFeatures", element: <MyToolFeatures /> },
        { path: "/MyTools/Edit/:id", element: <EditMyTool /> },
        { path: "/MyRequests", element: <MyRequests /> },
    ];

    // User routes that should be accessible by both user and admin roles
    const userAccessiblePaths = ["/MySubmissions", "/MyTools", "/MyCriteria", "/MyToolFeatures", "/MyRequests", "/SubmitToolPassport", "/SubmitCriteria", "/SubmitToolFeature"];

    // Check if user has access based on components permission (if configured)
    const hasComponentAccess = (path) => {
        // Admin role has access to all routes
        const userRole = auth?.role?.toLowerCase();
        if (userRole === 'admin') {
            return true;
        }
        // If no components defined in auth, or empty array, allow access (fall back to role-based only)
        if (!auth?.components || !Array.isArray(auth.components) || auth.components.length === 0) {
            return true;
        }
        // Home, About, Help, ToolsCatalog, ResearchPublications, Research, ChangePassword, and user submission paths are always accessible
        if (path === "/Home" || path === "/About" || path === "/Help" || path === "/ToolsCatalog" || path === "/ResearchPublications" || path.startsWith("/Research") || path.includes("/ChangePassword")) return true;
        // User accessible paths are always allowed for users with those roles
        // Check if path starts with any of the user accessible paths (handles /MyTools/Edit/:id etc.)
        const basePath = path.replace(/\/:[^/]+/g, '');
        if (userAccessiblePaths.some(uap => basePath === uap || basePath.startsWith(uap + "/"))) return true;
        // Check if base path is in allowed components
        return auth.components.some(comp => comp.startsWith(basePath) || basePath.startsWith(comp));
    };

    // Get user role for route protection
    const userRole = auth?.role?.toLowerCase();

    // Render routes using React Router's Routes component
    const renderContent = () => {
        // Direct rendering for always-accessible pages
        // This bypasses React Router context issues when MainLayout is rendered by SmartRoute
        if (location.pathname === '/Help') {
            return <HelpContent />;
        }
        if (location.pathname === '/About') {
            return <AboutContent />;
        }
        if (location.pathname === '/ToolsCatalog') {
            return <ToolsCatalogContent />;
        }
        if (location.pathname === '/ResearchPublications') {
            return <ResearchPublicationsContent />;
        }
        // Direct rendering for paths served by SmartRoute - bypasses nested Routes
        // scoping issue when MainLayout is rendered by SmartRoute (parent route consumes the path)
        if (location.pathname === '/' || location.pathname === '/Home') {
            return <Home />;
        }
        if (location.pathname.startsWith('/Tools/')) {
            const parts = location.pathname.split('/').filter(Boolean);
            if (parts.length === 4) {
                return <HomeToolsLegacy />;
            }
            if (parts.length === 3) {
                return <HomeSubcategories />;
            }
        }

        return (
            <Routes>
                {/* Shared routes accessible by both admin and user roles */}
                {sharedRoutes.filter(r => r.path !== '/About' && r.path !== '/Help' && r.path !== '/ToolsCatalog').map((route, index) => (
                    (userRole === 'user' || userRole === 'admin') && hasComponentAccess(route.path) ? (
                        <Route key={`shared-${index}`} path={route.path} element={route.element} />
                    ) : null
                ))}

                {/* Admin-only routes */}
                {adminRoutes.map((route, index) => (
                    userRole === 'admin' && hasComponentAccess(route.path) ? (
                        <Route key={`admin-${index}`} path={route.path} element={route.element} />
                    ) : null
                ))}

                {/* User routes (accessible by both user and admin) */}
                {userRoutes.map((route, index) => (
                    (userRole === 'user' || userRole === 'admin') && hasComponentAccess(route.path) ? (
                        <Route key={`user-${index}`} path={route.path} element={route.element} />
                    ) : null
                ))}

                {/* Fallback route for not found */}
                <Route path="*" element={<NotFound />} />
            </Routes>
        );
    };

    return (
        <div className="d-flex flex-column min-vh-100">
            <header>
                <div className="container-fluid p-0">
                    <TopHeader toggleSidebar={toggleSidebar} />
                </div>
            </header>
            <div className="d-flex flex-grow-1 overflow-hidden">
                {!isHome && !isTools && !isAbout && !isHelp && !isToolsCatalog && !isResearchPublications && !isResearch && (
                    <aside className={`col-lg-2 d-lg-block bg-body text-dark border-end sidebar ${isSidebarOpen ? '' : 'd-none'}`} style={{ minHeight: '100%' }}>
                        <Sidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
                    </aside>
                )}
                <main className={`col-lg-${(isHome || isTools || isAbout || isHelp || isToolsCatalog || isResearchPublications || isResearch) ? '12' : '10'} col-12 p-3 main-content flex-grow-1`} style={{ overflowY: 'auto' }}>
                    {renderContent()}
                </main>
            </div>
            <footer className="mt-auto">
                <div className="container-fluid p-0">
                    <MainFooter />
                </div>
            </footer>
        </div>
    );
};
export default observer(MainLayout);